import { toast } from 'react-toastify';
import call from '.';

/**
 * Pull a human-readable message out of an axios error.
 *
 * The API answers with `{ status, message, detail }`; `detail` is often a DRF
 * field-error object, which is worth surfacing because it says *which* field
 * was rejected.
 */
export function errorMessage(error, fallback = 'Sorry, something went wrong') {
  const data = error?.response?.data;
  if (!data) return fallback;
  if (data.message) return data.message;
  if (typeof data.detail === 'string') return data.detail;
  if (data.detail && typeof data.detail === 'object') {
    const [field, messages] = Object.entries(data.detail)[0] ?? [];
    if (field) return `${field}: ${[].concat(messages).join(', ')}`;
  }
  return fallback;
}

/**
 * Run one API call through the request/success/error dispatch cycle.
 *
 * Every thunk in this app repeated the same ~40 lines of status-code branching;
 * this centralises it so a thunk is just its endpoint plus its action types.
 *
 * Resolves to `{ ok, data, message }` so a component can `await dispatch(...)`
 * and only close a dialog or refetch a list once the write actually landed.
 */
export const apiAction =
  ({
    types: [REQUEST, SUCCESS, ERROR],
    method = 'get',
    url,
    data = null,
    successMessage,
    toastOnSuccess = false,
    requestMeta = {},
    onSuccess,
  }) =>
  async (dispatch) => {
    dispatch({ type: REQUEST, isLoading: true, isSuccess: false, ...requestMeta });
    try {
      const response = await call(method, url, data);
      const body = response?.data ?? {};
      if (body.status) {
        dispatch({ type: SUCCESS, isLoading: false, isSuccess: true, payload: body, message: body.message });
        if (toastOnSuccess) toast.success(successMessage || body.message);
        if (onSuccess) onSuccess(body);
        return { ok: true, data: body, message: body.message };
      }
      toast.error(body.message || 'Sorry, something went wrong');
      dispatch({ type: ERROR, isLoading: false, isSuccess: false, message: body.message });
      return { ok: false, data: body, message: body.message };
    } catch (error) {
      const message = errorMessage(error);
      console.error('API error:', error);
      dispatch({ type: ERROR, isLoading: false, isSuccess: false, message });
      toast.error(message);
      return { ok: false, message };
    }
  };

export default apiAction;
